import { Button, Input, SearchInput, Surface } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { PortalAssortmentRow } from '@warehouse/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PAGE_SIZE } from '../../../lib/list-query';
import { ORDER_QUERY_KEYS, salesOrdersApi, type OrderLineInput } from '../api';

/**
 * Article + quantity rows, shared by the admin order form and the staff-order
 * form. Quantities are entered in the article's SALES unit (cartons, kilos);
 * the server converts to base units once, at the boundary.
 */

export interface EditableLine extends OrderLineInput {
  articleNo: string;
  name: string;
  unitCode: string;
}

export interface OrderLineEditorProps {
  lines: EditableLine[];
  onChange: (lines: EditableLine[]) => void;
  disabled?: boolean;
}

export function OrderLineEditor({ lines, onChange, disabled = false }: OrderLineEditorProps): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const [search, setSearch] = useState('');

  const listState = useMemo(
    () => ({ q: search.trim() || undefined, pageSize: DEFAULT_PAGE_SIZE, page: 1 }),
    [search],
  );

  const { data, isLoading } = useQuery({
    queryKey: ORDER_QUERY_KEYS.orderArticles(listState),
    queryFn: () => salesOrdersApi.articles(listState),
    enabled: search.trim().length > 0,
  });

  const chosen = new Set(lines.map((l) => l.articleId));

  function addArticle(article: PortalAssortmentRow): void {
    if (chosen.has(article.articleId)) return;
    onChange([
      ...lines,
      {
        articleId: article.articleId,
        qty: '1',
        unitId: article.unitId,
        articleNo: article.articleNo,
        name: article.name,
        unitCode: article.unitCode,
      },
    ]);
    setSearch('');
  }

  return (
    <div className="flex flex-col gap-3">
      <SearchInput
        variant="outline"
        value={search}
        onChange={setSearch}
        onClear={() => {
          setSearch('');
        }}
        placeholder={t('salesOrders:create.articlePlaceholder')}
        aria-label={t('salesOrders:create.articlePlaceholder')}
        clearLabel={t('common:actions.clearFilters')}
        disabled={disabled}
      />

      {search.trim().length > 0 ? (
        <Surface className="max-h-56 overflow-y-auto p-1" data-testid="article-search-results">
          {isLoading ? (
            <p className="px-3 py-2 text-sm text-ink-muted">{t('common:common.loading')}</p>
          ) : (data?.items.length ?? 0) === 0 ? (
            <p className="px-3 py-2 text-sm text-ink-muted">{t('common:common.noResults')}</p>
          ) : (
            <ul>
              {data?.items.map((article) => (
                <li key={article.articleId}>
                  <button
                    type="button"
                    disabled={chosen.has(article.articleId)}
                    onClick={() => {
                      addArticle(article);
                    }}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-brand-600/[0.06] disabled:opacity-50"
                  >
                    <span className="min-w-0">
                      <span className="font-mono text-xs text-ink-muted">{article.articleNo}</span>
                      <span className="ml-2">{article.name}</span>
                    </span>
                    <span className="shrink-0 text-xs text-ink-muted">
                      {chosen.has(article.articleId) ? t('salesOrders:portal.inCart') : article.unitCode}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Surface>
      ) : null}

      {lines.length === 0 ? (
        <p className="rounded-lg border border-dashed border-surface-border px-4 py-6 text-center text-sm text-ink-muted">
          {t('salesOrders:create.noLines')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="order-lines">
          {lines.map((line, index) => (
            <li key={line.articleId} className="flex items-center gap-3 rounded-lg border border-surface-border px-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>
                <span className="ml-2 text-sm">{line.name}</span>
              </span>
              <Input
                className="w-24"
                type="number"
                min="0"
                step="0.001"
                inputMode="decimal"
                value={line.qty}
                disabled={disabled}
                aria-label={`${t('salesOrders:create.qty')} ${line.articleNo}`}
                onChange={(event) => {
                  const next = [...lines];
                  next[index] = { ...line, qty: event.target.value };
                  onChange(next);
                }}
              />
              <span className="w-12 shrink-0 text-xs text-ink-muted">{line.unitCode}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={`${t('salesOrders:actions.removeLine')} ${line.articleNo}`}
                onClick={() => {
                  onChange(lines.filter((l) => l.articleId !== line.articleId));
                }}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {lines.length > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-ink-muted">
          <Plus className="size-3" aria-hidden />
          {t('salesOrders:actions.addLine')}
        </p>
      ) : null}
    </div>
  );
}
